import {
    ConfigitAceQuoteLine,
    ConfigitEngine,
    ConfigitModelQuoteLine,
    LineType,
    QuotationQuoteLine,
    QuoteAssignment,
} from '../models/quote.model';
import { Quote } from '../services/quote.service';
import { ConfigitAceAssignment } from '../models/configit-ace.model';
import { ConfigitAssignment } from '../components/questionnaire/configit-quest-adapter/configit-types.interface';

export abstract class QuoteLineAdapter<
    A extends QuoteAssignment | ConfigitAceAssignment,
    L extends QuotationQuoteLine
> {
    protected abstract engine(): ConfigitEngine;

    public abstract createConfigurationLine(material: string, type: LineType, assignments: A[], quote: Quote): L;

    public abstract setLineAssignments(assignments: A[], line: L): void;

    public getLineByMaterial(quote: Quote, material: string): L {
        return <L>quote.quotationQuote.offer.extraData.lines.find((l) => l.material === material);
    }

    protected getLineByType(quote: Quote, type: LineType): L {
        return <L>quote.quotationQuote.offer.extraData.lines.find((l) => l.type === type);
    }

    protected addConfigurationLine(type: LineType, material: string, assignments: A[], quote: Quote) {
        const line = <L>{ type, assignments: [], material, engine: this.engine() };
        this.setLineAssignments(assignments, line);
        const extraData = quote.quotationQuote.offer.extraData;
        // if there were any lines of that type before, remove them
        extraData.lines = extraData.lines.filter((l) => l.type !== type);
        extraData.lines.push(line);
    }
}

export class ConfigitAceQuoteLineAdapter extends QuoteLineAdapter<ConfigitAceAssignment, ConfigitAceQuoteLine> {
    protected engine(): ConfigitEngine {
        return ConfigitEngine.ace;
    }

    public createConfigurationLine(
        material: string,
        type: LineType,
        assignments: ConfigitAceAssignment[],
        quote: Quote
    ): ConfigitAceQuoteLine {
        if (!quote) {
            return undefined;
        }

        let line = this.getLineByType(quote, type);
        if (line) {
            // merge assignments into line.assignments
            const replaced = line.assignments.map((a1) => ({
                ...a1,
                ...assignments.find((a2) => a2.variableId === a1.variableId),
            }));
            const newAssignments = assignments.filter(
                (a1) => !line.assignments.some((a2) => a2.variableId === a1.variableId)
            );
            this.setLineAssignments([...replaced, ...newAssignments], line);
        } else {
            this.addConfigurationLine(type, material, assignments, quote);
        }
        return line;
    }

    public setLineAssignments(assignments: ConfigitAceAssignment[], line: ConfigitAceQuoteLine): void {
        line.assignments = assignments;
    }
}

export class ConfigitModelQuoteLineAdapter extends QuoteLineAdapter<ConfigitAssignment, ConfigitModelQuoteLine> {
    protected engine(): ConfigitEngine {
        return ConfigitEngine.model;
    }

    public createConfigurationLine(
        material: string,
        type: LineType,
        assignments: ConfigitAssignment[],
        quote: Quote
    ): ConfigitModelQuoteLine {
        let line = this.getLineByType(quote, type);
        if (line) {
            // merge assignments into line.assignments
            const replaced = line.assignments.map((a1) => ({
                ...a1,
                ...assignments.find((a2) => a2.variableName === a1.variableName),
            }));
            const newAssignments = assignments.filter(
                (a1) => !line.assignments.some((a2) => a2.variableName === a1.variableName)
            );
            this.setLineAssignments([...replaced, ...newAssignments], line);
        } else {
            this.addConfigurationLine(type, material, assignments, quote);
        }
        return line;
    }

    public setLineAssignments(assignments: ConfigitAssignment[], line: ConfigitModelQuoteLine): void {
        line.assignments = assignments
            .filter((assignment) => !assignment.variableName.startsWith('SapData'))
            .map(({ variableName, valueName, isUserAssignment, isDefault }) => ({
                variableName,
                valueName,
                isUserAssignment,
                isDefault,
            }));
    }
}
